import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  PinterestShareButton,
} from 'react-share';

import Link from '../../../Core/Link';

import { Social, CopyPopup } from './styles';

/*
 * className: {string} className for the social share passed in when styled via styledComponents
 * social: {array} of objs that have the img and the link to the social media account
 * shareURL: {string} the location provided by gatsby, it is the window.location.href value of the page, and is used in the social share buttons
 * location: {string} the same as shareURL, however if there is no shareURL we dont use share but actual links
 */

function SocialShare({
  className, social, shareUrl, location,
}) {
  const [clipboard, setClipboard] = useState(false);
  const [timeoutID, setTimeoutID] = useState(null);

  // * If there is already a timeout, clear it
  // * If the popup isnt already displaying, then update state to display it, and start a timeout
  useEffect(() => {
    if (timeoutID) clearTimeout(timeoutID);
    if (clipboard) {
      const id = setTimeout(() => {
        setClipboard(!clipboard);
      }, 700);
      setTimeoutID(id);
    }
  }, [clipboard]);

  return (
    <Social className={['no-bullets', className]}>
      {social.map(
        (
          {
            type, imageIcon, imageIconSharp, link, queryString, ariaLabel,
          },
          index,
        ) => {
          switch (type[0].text) {
            case 'Facebook':
              return (
                <li key={`${type[0].text}-${index.toString()}`}>
                  {shareUrl ? (
                    <FacebookShareButton url={shareUrl}>
                      <img
                        src={imageIconSharp.publicURL}
                        alt={imageIcon.altTag}
                      />
                    </FacebookShareButton>
                  ) : (
                    <Link
                      to={link ? link.url : '/'}
                      ariaLabel={ariaLabel}
                      queryString={queryString}
                    >
                      <img
                        src={imageIconSharp.publicURL}
                        alt={imageIcon.altTag}
                      />
                    </Link>
                  )}
                </li>
              );
            case 'Linked In':
              return (
                <li key={`${type[0].text}-${index.toString()}`}>
                  {shareUrl ? (
                    <LinkedinShareButton url={shareUrl}>
                      <img
                        src={imageIconSharp.publicURL}
                        alt={imageIcon.altTag}
                      />
                    </LinkedinShareButton>
                  ) : (
                    <Link
                      to={link ? link.url : '/'}
                      ariaLabel={ariaLabel}
                      queryString={queryString}
                    >
                      <img
                        src={imageIconSharp.publicURL}
                        alt={imageIcon.altTag}
                      />
                    </Link>
                  )}
                </li>
              );
            case 'Pinterest':
              return (
                <li key={`${type[0].text}-${index.toString()}`}>
                  {shareUrl ? (
                    <PinterestShareButton url={shareUrl}>
                      <img
                        src={imageIconSharp.publicURL}
                        alt={imageIcon.altTag}
                      />
                    </PinterestShareButton>
                  ) : (
                    <Link
                      to={link ? link.url : '/'}
                      ariaLabel={ariaLabel}
                      queryString={queryString}
                    >
                      <img
                        src={imageIconSharp.publicURL}
                        alt={imageIcon.altTag}
                      />
                    </Link>
                  )}
                </li>
              );
            case 'Twitter':
              return (
                <li key={`${type[0].text}-${index.toString()}`}>
                  {shareUrl ? (
                    <TwitterShareButton url={shareUrl}>
                      <img
                        src={imageIconSharp.publicURL}
                        alt={imageIcon.altTag}
                      />
                    </TwitterShareButton>
                  ) : (
                    <Link
                      to={link ? link.url : '/'}
                      ariaLabel={ariaLabel}
                      queryString={queryString}
                    >
                      <img
                        src={imageIconSharp.publicURL}
                        alt={imageIcon.altTag}
                      />
                    </Link>
                  )}
                </li>
              );
            case 'Share':
              return (
                <li key={`${type[0].text}-${index.toString()}`}>
                  <CopyToClipboard
                    text={shareUrl || location}
                    onCopy={() => setClipboard(true)}
                  >
                    <button type="button" aria-label="Copy to clipboard">
                      <img
                        src={imageIconSharp.publicURL}
                        alt={imageIcon.altTag}
                      />
                    </button>
                  </CopyToClipboard>
                  <CopyPopup className={clipboard && 'is-active'}>
                    Copied to clipboard
                  </CopyPopup>
                </li>
              );
            default:
              return null;
          }
        },
      )}
    </Social>
  );
}

SocialShare.propTypes = {
  className: PropTypes.string,
  social: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  shareUrl: PropTypes.string,
  location: PropTypes.string,
};

SocialShare.defaultProps = {
  className: null,
  shareUrl: null,
  location: null,
};

export default SocialShare;
